/**
 * ARBI CLI — entry point
 *
 * Wires all commands into commander and runs.
 */

// Suppress SDK middleware logging (console.debug/info go to stdout in Node.js, polluting piped output)
console.debug = () => {}
const _origInfo = console.info
console.info = (...args: unknown[]) => {
  if (typeof args[0] === 'string' && args[0].startsWith('[API]')) return
  _origInfo(...args)
}

import { Command } from 'commander'
import { getCompletions } from './completion.js'
import { registerConfigCommand } from './commands/config-cmd.js'
import { registerLoginCommand } from './commands/login.js'
import { registerRegisterCommand } from './commands/register.js'
import { registerLogoutCommand } from './commands/logout.js'
import { registerStatusCommand } from './commands/status.js'
import { registerWorkspacesCommand } from './commands/workspaces.js'
import { registerDocsCommand } from './commands/docs.js'
import { registerUploadCommand } from './commands/upload.js'
import { registerDownloadCommand } from './commands/download.js'
import { registerAskCommand } from './commands/ask.js'
import { registerCiteCommand } from './commands/cite.js'
import { registerFindCommand } from './commands/find.js'
import { registerWatchCommand } from './commands/watch.js'
import { registerContactsCommand } from './commands/contacts.js'
import { registerDmCommand } from './commands/dm.js'
import { registerTagsCommand } from './commands/tags.js'
import { registerDoctagsCommand } from './commands/doctags.js'
import { registerConversationsCommand } from './commands/conversations.js'
import { registerSettingsCommand } from './commands/settings.js'
import { registerAgentconfigCommand } from './commands/agentconfig.js'
import { registerHealthCommand } from './commands/health.js'
import { registerTuiCommand } from './commands/tui.js'
import { registerUpdateCommand } from './commands/update.js'
import { registerQuickstartCommand } from './commands/quickstart.js'
import { registerAgentCreateCommand } from './commands/agent-create.js'
import { registerAgentCommand } from './commands/agent.js'
import { registerTaskCommand } from './commands/task.js'
import { registerCompletionCommand } from './commands/completion.js'
import { registerListenCommand } from './commands/listen.js'
import { registerLocalCommand } from './commands/local.js'
import { registerSessionCommand } from './commands/session.js'
import { registerNotificationsCommand } from './commands/notifications.js'
import { registerProjectCommand } from './commands/project.js'
import { registerFilesCommand } from './commands/files.js'
import { registerUsageCommand } from './commands/usage.js'
import { registerAuthCommand } from './commands/auth.js'
import { registerSkillsCommand } from './commands/skills.js'

const program = new Command()

declare const __CLI_VERSION__: string

program
  .name('arbi')
  .description('ARBI CLI — interact with ARBI from the terminal')
  .version(__CLI_VERSION__)
  // Self-correcting CLI: any unknown command / unknown option / missing
  // required argument auto-prints the relevant help screen so an agent
  // can read it and retry without a second round-trip to fetch `--help`.
  .showHelpAfterError(true)
  .showSuggestionAfterError(true)

registerConfigCommand(program)
registerLoginCommand(program)
registerRegisterCommand(program)
registerLogoutCommand(program)
registerStatusCommand(program)
registerWorkspacesCommand(program)
registerDocsCommand(program)
registerUploadCommand(program)
registerDownloadCommand(program)
registerAskCommand(program)
registerCiteCommand(program)
registerFindCommand(program)
registerWatchCommand(program)
registerContactsCommand(program)
registerDmCommand(program)
registerTagsCommand(program)
registerDoctagsCommand(program)
registerConversationsCommand(program)
registerSettingsCommand(program)
registerAgentconfigCommand(program)
registerHealthCommand(program)
registerTuiCommand(program)
registerUpdateCommand(program)
registerQuickstartCommand(program)
registerAgentCreateCommand(program)
registerAgentCommand(program)
registerTaskCommand(program)
registerCompletionCommand(program)
registerListenCommand(program)
registerSessionCommand(program)
registerLocalCommand(program)
registerNotificationsCommand(program)
registerProjectCommand(program)
registerFilesCommand(program)
registerUsageCommand(program)
registerAuthCommand(program)
registerSkillsCommand(program)

// ── Help-screen grouping ────────────────────────────────────────────────────
// Commander v14's `helpGroup()` keeps the alphabetical-ish registration order
// from being the user's first impression. We bucket the 30+ top-level
// commands by INTENT so an agent searching the help can find the verb they
// want without `grep --help -A 100`.
applyHelpGroups(program, {
  'Getting started:': [
    'quickstart',
    'login',
    'register',
    'logout',
    'status',
    'config',
    'completion',
  ],
  'Workspaces:': ['workspaces', 'workspace', 'project'],
  'Documents:': ['docs', 'doc', 'add', 'upload', 'download'],
  'Search & ask:': ['ask', 'find', 'cite', 'watch'],
  'Tagging:': ['tags', 'doctags'],
  'Conversations & messaging:': ['convo', 'dm', 'listen', 'notifications', 'contacts'],
  'Agents & tasks:': ['agent', 'agent-create', 'task', 'agentconfig', 'session'],
  'Account:': ['auth', 'usage', 'files'],
  'System:': ['health', 'models', 'settings', 'tui', 'local', 'update'],
})

function applyHelpGroups(prog: Command, groups: Record<string, string[]>): void {
  for (const [groupLabel, names] of Object.entries(groups)) {
    for (const name of names) {
      const cmd = prog.commands.find((c) => c.name() === name)
      // Commander 14 ships `helpGroup` on the Command instance; tolerate the
      // method being absent (older shims, type-only stub) without crashing.
      const hg = (cmd as unknown as { helpGroup?: (label: string) => unknown })?.helpGroup
      if (cmd && typeof hg === 'function') {
        hg.call(cmd, groupLabel)
      }
    }
  }
}

// ── Tab completion (runs after all commands are registered) ─────────────────
const completionIdx = process.argv.indexOf('--get-completions')
if (completionIdx !== -1) {
  const line = process.argv[completionIdx + 1] ?? ''
  const candidates = getCompletions(program, line)
  if (candidates.length > 0) process.stdout.write(candidates.join('\n') + '\n')
  process.exit(0)
}

program.parse()
